import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import purchaseOrdersService from '../services/purchaseOrdersService';
import accountsPayableService from '../services/accountsPayableService';
import ReceivePurchaseOrderModal from './ReceivePurchaseOrderModal';
import AccountsPayable from './AccountsPayable';
import {
  ShoppingCart, Package, Truck, CheckCircle, Clock, AlertCircle,
  Search, Filter, Plus, X, Loader, Edit, Trash2, Eye, DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  PENDING: { label: 'Pendente', color: 'yellow', icon: Clock },
  APPROVED: { label: 'Aprovado', color: 'blue', icon: CheckCircle },
  ORDERED: { label: 'Pedido', color: 'purple', icon: Truck },
  RECEIVED: { label: 'Recebido', color: 'green', icon: CheckCircle },
  CANCELLED: { label: 'Cancelado', color: 'red', icon: AlertCircle }
};

const PurchaseOrders = ({ projectId, projectName, onClose }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('orders');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [orderToReceive, setOrderToReceive] = useState(null);

  // Buscar pedidos de compra
  const { data: ordersData, isLoading: loadingOrders } = useQuery({
    queryKey: ['purchase-orders', projectId],
    queryFn: () => purchaseOrdersService.getByProject(projectId),
    enabled: !!projectId
  });

  // Buscar itens do orçamento para comprar
  const { data: itemsData, isLoading: loadingItems } = useQuery({
    queryKey: ['budget-items-to-buy', projectId],
    queryFn: () => purchaseOrdersService.getBudgetItemsToPurchase(projectId),
    enabled: !!projectId && activeTab === 'create'
  });

  // Mutation para criar pedido
  const createMutation = useMutation({
    mutationFn: (data) => purchaseOrdersService.create(projectId, data),
    onSuccess: () => {
      toast.success('Pedido de compra criado com sucesso!');
      queryClient.invalidateQueries(['purchase-orders', projectId]);
      queryClient.invalidateQueries(['budget-items-to-buy', projectId]);
      setShowCreateModal(false);
      setSelectedItems([]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao criar pedido');
    }
  });

  // Mutation para atualizar status
  const updateStatusMutation = useMutation({
    mutationFn: ({ orderId, status }) => purchaseOrdersService.updateStatus(orderId, status),
    onSuccess: () => {
      toast.success('Status atualizado!');
      queryClient.invalidateQueries(['purchase-orders', projectId]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao atualizar status');
    }
  });

  const orders = ordersData?.data?.orders || [];
  const itemsToBuy = itemsData?.data?.items || [];

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      }
      return [...prev, item];
    });
  };

  const handleCreateOrder = () => {
    if (selectedItems.length === 0) {
      toast.error('Selecione pelo menos um item');
      return;
    }

    const firstSupplierId = selectedItems[0]?.supplier_id;
    if (!firstSupplierId) {
      toast.error('Os itens não possuem fornecedor vinculado. Cadastre o fornecedor nos itens do orçamento.');
      return;
    }

    const hasMixedSuppliers = selectedItems.some(i => i.supplier_id !== firstSupplierId);
    if (hasMixedSuppliers) {
      toast.error('Todos os itens devem ter o mesmo fornecedor para criar um pedido.');
      return;
    }

    createMutation.mutate({
      supplier_id: firstSupplierId,
      items: selectedItems.map(i => ({
        item_id: i.id,
        quantity: i.quantity_to_buy || i.quantity,
        unit_price: i.unit_price
      }))
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pedidos de Compra</h2>
              <p className="text-sm text-gray-600">{projectName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Novo Pedido
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-3 font-medium ${activeTab === 'orders' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          >
            Pedidos ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('items')}
            className={`px-4 py-3 font-medium ${activeTab === 'items' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          >
            Itens do Orçamento
          </button>
          <button
            onClick={() => setActiveTab('parcels')}
            className={`px-4 py-3 font-medium ${activeTab === 'parcels' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
          >
            <DollarSign className="w-4 h-4 inline mr-1" />
            Parcelas
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {activeTab === 'orders' && (
            <div>
              {loadingOrders ? (
                <div className="flex items-center justify-center p-8">
                  <Loader className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                  Nenhum pedido de compra criado ainda.
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map(order => {
                    const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.PENDING;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <div key={order.id} className="card p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${statusConfig.bg || 'bg-gray-100'}`}>
                            <StatusIcon className={`w-5 h-5 text-${statusConfig.color}-600`} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Pedido #{order.id}</h4>
                            <p className="text-sm text-gray-500">
                              {order.items?.length || 0} itens - {formatCurrency(order.total_value)}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(order.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {order.status !== 'RECEIVED' && order.status !== 'CANCELLED' && (
                            <button
                              onClick={() => setOrderToReceive(order)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Receber
                            </button>
                          )}
                          <span className={`px-3 py-1 rounded-full text-sm font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                            {statusConfig.label}
                          </span>
                          <select
                            value={order.status}
                            onChange={(e) => updateStatusMutation.mutate({ orderId: order.id, status: e.target.value })}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="PENDING">Pendente</option>
                            <option value="APPROVED">Aprovado</option>
                            <option value="ORDERED">Pedido</option>
                            <option value="RECEIVED">Recebido</option>
                            <option value="CANCELLED">Cancelado</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div>
              {loadingItems ? (
                <div className="flex items-center justify-center p-8">
                  <Loader className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : itemsToBuy.length === 0 ? (
                <div className="text-center p-8 text-gray-500">
                  Nenhum item para comprar no orçamento.
                </div>
              ) : (
                <div className="space-y-2">
                  {itemsToBuy.map(item => (
                    <div
                      key={item.id}
                      className={`p-3 border rounded-lg flex items-center justify-between ${selectedItems.find(i => i.id === item.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!selectedItems.find(i => i.id === item.id)}
                          onChange={() => toggleItem(item)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{item.item_name}</p>
                          <p className="text-sm text-gray-500">
                            Qtd: {item.quantity_to_buy || item.quantity} - {formatCurrency(item.unit_price)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {formatCurrency((item.quantity_to_buy || item.quantity) * item.unit_price)}
                        </p>
                        {item.supplier_name && (
                          <p className="text-sm text-gray-500">{item.supplier_name}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'parcels' && (
            <AccountsPayable
              projectId={projectId}
              projectName={projectName}
              onClose={() => {}}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Fechar
          </button>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">Criar Pedido de Compra</h3>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-600 mb-4">
                  Selecione os itens do orçamento que desea comprar:
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {itemsToBuy.map(item => (
                    <div
                      key={item.id}
                      className={`p-3 border rounded-lg flex items-center justify-between ${selectedItems.find(i => i.id === item.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!selectedItems.find(i => i.id === item.id)}
                          onChange={() => toggleItem(item)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{item.item_name}</p>
                          <p className="text-sm text-gray-500">
                            Qtd: {item.quantity_to_buy || item.quantity}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {formatCurrency((item.quantity_to_buy || item.quantity) * item.unit_price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg flex justify-between">
                  <span className="font-medium">Total:</span>
                  <span className="font-bold text-lg">
                    {formatCurrency(selectedItems.reduce((sum, item) => sum + ((item.quantity_to_buy || item.quantity) * item.unit_price), 0))}
                  </span>
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={selectedItems.length === 0 || createMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Criando...' : 'Criar Pedido'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Receive Order Modal */}
        {orderToReceive && (
          <ReceivePurchaseOrderModal
            order={orderToReceive}
            projectId={projectId}
            onClose={() => setOrderToReceive(null)}
            onSuccess={() => {
              queryClient.invalidateQueries(['purchase-orders', projectId]);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PurchaseOrders;